import { faExclamation } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Alert, Box, Group, Text, useMantineTheme } from '@mantine/core'
import { Dropzone as MantineDropzone, FileWithPath } from '@mantine/dropzone'
import { nanoid } from 'nanoid'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DropzoneProps } from '../../types/drop-zone'
import { DisplayAsset, MIME_TYPE } from '../../types/files'
import { blobToBase64, formatBytes, getFileExtension } from '../../utils'
import Accept from './StateChildren/Accept'
import Idle from './StateChildren/Idle'
import Reject from './StateChildren/Reject'

const Dropzone = ({
  acceptedMimeTypes,
  allowedFileSize,
  currentFileCount,
  maxFiles,
  onAssetAdded,
  acceptChildren,
  idleChildren,
  rejectChildren,
  innerGroupOverrideProps = {},
  dropzoneOverrideProps = {},
}: DropzoneProps): React.ReactElement => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const { t } = useTranslation('file_uploads')
  const theme = useMantineTheme()

  const handleDrop = async (acceptedFiles: FileWithPath[]): Promise<void> => {
    const transformedAssets: DisplayAsset[] = []

    try {
      setError(false)
      setIsLoading(true)
      await Promise.all(
        acceptedFiles.map(async (file) => {
          const base64 = await blobToBase64(file)

          transformedAssets.push({
            name: file.name,
            id: nanoid(),
            extension: getFileExtension(file.name),
            type: file.type as MIME_TYPE,
            data: base64,
          })
        }),
      )
    } catch (_e) {
      setError(true)
    } finally {
      onAssetAdded(transformedAssets)
      setIsLoading(false)
    }
  }

  return (
    <>
      {error && (
        <Alert
          withCloseButton
          onClose={() => setError(false)}
          color="red"
          title={t('uploader.file_reader_error_title')}
          icon={<FontAwesomeIcon icon={faExclamation} />}
          mb="md"
        >
          <Text>{t('uploader.file_reader_error')}</Text>
        </Alert>
      )}
      <MantineDropzone
        p="xs"
        {...dropzoneOverrideProps}
        onDrop={handleDrop}
        accept={acceptedMimeTypes}
        disabled={currentFileCount === maxFiles}
        loading={isLoading}
      >
        <Group
          position="center"
          style={{ minHeight: 0 }}
          spacing="xl"
          p="xs"
          {...innerGroupOverrideProps}
        >
          <MantineDropzone.Accept>
            {acceptChildren ?? <Accept />}
          </MantineDropzone.Accept>
          <MantineDropzone.Reject>
            {rejectChildren ?? <Reject />}
          </MantineDropzone.Reject>
          <MantineDropzone.Idle>
            {idleChildren ?? <Idle acceptedMimeTypes={acceptedMimeTypes} />}
          </MantineDropzone.Idle>

          <Box>
            <Text size="sm" inline>
              {t('uploader.drag_here')}
            </Text>
            <Text
              size="xs"
              color="dimmed"
              inline
              mt={`calc(${theme.spacing.xs} / 2)`}
            >
              {t('uploader.remainingFilesWithCount', {
                count: maxFiles - currentFileCount,
              })}
            </Text>
            <Text
              size="xs"
              color="dimmed"
              inline
              mt={`calc(${theme.spacing.xs} / 2)`}
            >
              {t('uploader.file_size_limit', {
                maxFileSize: formatBytes(allowedFileSize),
              })}
            </Text>
          </Box>
        </Group>
      </MantineDropzone>
    </>
  )
}

export default Dropzone
